import React, { useState, useMemo, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Accordion, AccordionTab } from "primereact/accordion";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { RadioButton } from "primereact/radiobutton";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Fetch } from "../../../shared/Fetch";
export interface IForm {
    open: boolean;
    singleData: any;
    reload: boolean;
    setReload: React.Dispatch<React.SetStateAction<boolean>>;
    hideDialog: () => void;
    toast: any;
}

const schema = yup.object({
    jobTitleKeywords: yup.array().of(yup.string().oneOf(["current", "preferred", "previous"])).optional(),
    searchType: yup.string().oneOf(["Integrated", "Resume"]).required("Search type is required"),
    //socialProfiles: yup.string().oneOf(["all", "dice"]).required("Search type is required"),
    hasEmail: yup.boolean().optional(),
    hasPhoneNumber: yup.boolean().optional(),
    likelyToSwitch: yup.boolean().optional(),
    yearsExperienceMin: yup.number().integer().min(0).nullable().optional().transform((v, o) => (o === "" ? null : v)),
    yearsExperienceMax: yup.number().integer().min(0).nullable().optional().transform((v, o) => (o === "" ? null : v))
        .when("yearsExperienceMin", (min: any, s: any) => min != null ? s.min(min, "Max must be >= min") : s),
    educationDegree: yup.array().of(yup.string()).optional(),
    socialProfiles: yup.array().of(yup.string().oneOf(["all", "dice"])).optional(),
    language: yup.string().optional(),
    employmentType: yup.array().of(yup.string()).optional(),
    workPermitLocation: yup.array().of(yup.string().oneOf(["US", "CA", "UK"])).optional(),
    workPermit: yup.array().of(yup.string()).optional(),
    travelPreference: yup.number().nullable().optional().oneOf([null, 0, 25, 50, 75, 100]),
    currency: yup.string().oneOf(["USD", "GBP", "CAD", "EUR"]).optional(),
    compensationMin: yup.number().integer().min(0).nullable().optional().transform((v, o) => (o === "" ? null : v)),
    compensationMax: yup.number().integer().min(0).nullable().optional().transform((v, o) => (o === "" ? null : v))
        .when("compensationMin", (min: any, s: any) => min != null ? s.min(min, "Max must be >= min") : s),
    frequency: yup.string().oneOf(["hourly", "yearly"]).optional(),
    includeUnspecified: yup.boolean().optional(),
    onlyWithSecurityClearance: yup.boolean().optional(),
    dateResumeLastUpdated: yup.number().integer().min(1).max(2500).nullable().optional().transform((v, o) => (o === "" ? null : v)),
    lastActive: yup.number().integer().min(1).max(2500).nullable().optional().transform((v, o) => (o === "" ? null : v)),
});

export interface IFormValues {
    jobTitleKeywords: string[];
    skillsWithYoe: SkillWithYoe[];
    companyKeywords: string[];
    searchType: string;
    hasEmail: boolean;
    hasPhoneNumber: boolean;
    likelyToSwitch: boolean;
    yearsExperienceMin: number | null;
    yearsExperienceMax: number | null;
    educationDegree: string[];
    socialProfiles: string[];
    language: string;
    employmentType: string[];
    workPermitLocation: string[];
    workPermit: string[];
    travelPreference: number | null;
    currency: string;
    compensationMin: number | null;
    compensationMax: number | null;
    frequency: string;
    includeUnspecified: boolean;
    onlyWithSecurityClearance: boolean;
    dateResumeLastUpdated: number | null;
    lastActive: number | null;
}

const JOB_TYPE_OPTIONS = [{ label: "Current", value: "current" }, { label: "Preferred", value: "preferred" }, { label: "Previous", value: "previous" }];
const EDUCATION_OPTIONS = [{ label: "Bachelors", value: "bachelors" }, { label: "Masters", value: "masters" }, { label: "High School", value: "high school" }, { label: "Vocational School", value: "vocational school" }, { label: "MBA", value: "mba" }, { label: "Doctorate", value: "doctorate" }, { label: "Associates", value: "associate" }, { label: "Military Service", value: "military service" }];
const SOCIAL_OPTIONS = [{ label: "GitHub", value: "github" }, { label: "LinkedIn", value: "linkedin" }, { label: "X (Twitter)", value: "twitter" }, { label: "Facebook", value: "facebook" }, { label: "Stack Overflow", value: "stackoverflow" }, { label: "Instagram", value: "instagram" }, { label: "Meetup", value: "meetup" }];
const LANGUAGE_OPTIONS = [{ label: "English", value: "english" }, { label: "Hindi", value: "hindi" }, { label: "Spanish", value: "spanish" }, { label: "German", value: "german" }, { label: "Chinese", value: "chinese" }, { label: "Japanese", value: "japanese" }, { label: "Arabic", value: "arabic" }, { label: "Russian", value: "russian" }, { label: "Italian", value: "italian" }, { label: "Portuguese", value: "portuguese" }];
const EMPLOYMENT_OPTIONS = [{ label: "Full-Time", value: "full-time" }, { label: "Part-Time", value: "part-time" }, { label: "Contract - W2", value: "contract - w2" }, { label: "Contract - Independent", value: "contract - independent" }, { label: "Contract - Corp-to-Corp", value: "contract - corp-to-corp" }, { label: "Contract to Hire - W2", value: "contract to hire - w2" }, { label: "Contract to Hire - Independent", value: "contract to hire - independent" }, { label: "Contract to Hire - Corp-to-Corp", value: "contract to hire - corp-to-corp" }, { label: "1099 Employee", value: "1099 employee" }];
const COUNTRY_OPTIONS = [{ label: "USA", value: "US" }, { label: "Canada", value: "CA" }, { label: "UK", value: "UK" }];
const WORK_PERMIT_OPTIONS = [{ label: "US Citizen", value: "us citizenship" }, { label: "Green Card Holder", value: "green card" }, { label: "Have H1 Visa", value: "have h1" }, { label: "Need H1 Visa", value: "need h1" }, { label: "Employment Auth. Document", value: "employment auth document" }, { label: "TN Permit Holder", value: "tn permit holder" }, { label: "Canadian Citizen", value: "canadian citizen" }];
const CURRENCY_OPTIONS = [{ label: "USD ($)", value: "USD" }, { label: "GBP (£)", value: "GBP" }, { label: "CAD (CA$)", value: "CAD" }, { label: "EUR (€)", value: "EUR" }];
const FREQUENCY_OPTIONS = [{ label: "Yearly", value: "yearly" }, { label: "Hourly", value: "hourly" }];
const TRAVEL_OPTIONS = [{ id: "travel-any", value: null, label: "Any" }, { id: "travel-25", value: 25, label: "Up to 25%" }, { id: "travel-50", value: 50, label: "Up to 50%" }, { id: "travel-75", value: 75, label: "Up to 75%" }, { id: "travel-100", value: 100, label: "Up to 100%" }, { id: "travel-none", value: 0, label: "Prefers No Travel" }];
const SWITCH_OPTIONS = [{ label: "Most Likely to Switch", value: "most" }, { label: "Possibly Willing to Switch", value: "possibly" }];
const WORK_SETTING_OPTIONS = [{ id: "remote", value: "REMOTE", label: "Remote" }, { id: "onsite", value: "ONSITE", label: "On-Site" }, { id: "hybrid", value: "HYBRID", label: "Hybrid" }];

interface FilterTag {
    id: string;
    label: string;
    filled: boolean;
    onRemove: () => void;
}

const FilterSummaryBar = ({ tags }: { tags: FilterTag[] }) => {
    const [showAll, setShowAll] = useState(false);
    if (tags.length === 0) return null;

    const visible = showAll ? tags : tags.slice(0, 5);
    const hiddenCount = tags.length - 5;

    return (
        <div style={{ border: "1px solid #dee2e6", borderRadius: 8, padding: "12px 16px", backgroundColor: "#fff", marginBottom: 16 }}>
            <div className="flex flex-wrap align-items-center gap-2">

                {visible.map((tag) => (
                    <span key={tag.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, fontSize: 13, fontWeight: 500, backgroundColor: tag.filled ? "#0d7377" : "#fff", color: tag.filled ? "#fff" : "#374151", border: tag.filled ? "none" : "1.5px solid #9ca3af", whiteSpace: "nowrap" }}>
                        {tag.label}
                        <button type="button" onClick={tag.onRemove} aria-label={`Remove ${tag.label}`} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 14, fontWeight: 700, color: tag.filled ? "rgba(255,255,255,0.85)" : "#6b7280" }}>  x </button>
                    </span>
                ))}

                {!showAll && hiddenCount > 0 && (
                    <button type="button" onClick={() => setShowAll(true)} style={{ background: "none", border: "none", cursor: "pointer", color: "#0d7377", fontWeight: 600, fontSize: 13 }}> + {hiddenCount} more </button>
                )}
                {showAll && hiddenCount > 0 && (
                    <button type="button" onClick={() => setShowAll(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#0d7377", fontWeight: 600, fontSize: 13 }}> Show less </button>
                )}

            </div>
        </div>
    );
};

interface TagInputProps {
    value: string;
    onChange: (v: string) => void;
    onAdd: () => void;
    placeholder: string;
    disabled?: boolean;
    className?: string;
}

interface SkillWithYoe {
    skill: string;
    years: number | null;
}

const TagInput = ({ value, onChange, onAdd, placeholder, disabled = false, className = "w-25rem" }: TagInputProps) => (
    <div className="flex align-items-center gap-2">
        <InputText value={value} placeholder={placeholder} className={className} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
        />
        <Button icon="pi pi-plus" type="button" className="p-button-outlined p-button-secondary flex-shrink-0" onClick={onAdd} disabled={!value.trim() || disabled}
        />
    </div>
);

const parseNumber = (value: any) => {
    const num = Number(value);
    return isNaN(num) ? null : num;
};

interface IProps {
    form2?: any,
    setForm2: (data: IFormValues) => void,
    setForm2Data: (data: any) => void,
    reload: boolean,
    setReload: (is: boolean) => void,
    toast: any,
    hideDialog?: () => void,
    loading: boolean,
    setLoading: (is: boolean) => void,
}

export const Form2 = (props: IProps) => {
    const [jobTitleInput, setJobTitleInput] = useState("");
    const [jobTitleList, setJobTitleList] = useState<string[]>([]);
    const [skillInput, setSkillInput] = useState("");
    const [skillList, setSkillList] = useState<string[]>([]);
    const [yoeInput, setYoeInput] = useState("");
    const [yoeList, setYoeList] = useState<string[]>([]);
    const [companyInput, setCompanyInput] = useState("");
    const [companyList, setCompanyList] = useState<string[]>([]);
    const [switchGroup, setSwitchGroup] = useState<string[]>([]);
    const [workSetting, setWorkSetting] = useState<string>("");

    const ALL_INDEXES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
    const [activeIndex, setActiveIndex] = useState<number | number[]>([]);
    const handleExpandAll = () => setActiveIndex([...ALL_INDEXES]);
    const handleCollapseAll = () => setActiveIndex([]);

    const [skillYoeInput, setSkillYoeInput] = useState("");
    const [skillsWithYoeList, setSkillsWithYoeList] = useState<SkillWithYoe[]>([]);

    const { control, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } = useForm<IFormValues>({
        resolver: yupResolver(schema) as any,
        defaultValues: { searchType: "Integrated", socialProfiles: ["dice"] }
    });

    const values = watch();
    const addToList = (
        input: string,
        setInput: (v: string) => void,
        setList: React.Dispatch<React.SetStateAction<string[]>>,
        isValid = true
    ) => {
        const v = input.trim();
        if (!v || !isValid) return;
        setList((prev) => [...prev, v]);
        setInput("");
    };

    const removeAt = (index: number, setList: React.Dispatch<React.SetStateAction<string[]>>) =>
        setList((prev) => prev.filter((_, i) => i !== index));


    const fieldError = (name: keyof IFormValues) =>
        (errors[name] as any)?.message as string | undefined;

    // const buildPayload = (data: FormValues) => {
    //     const p: Record<string, any> = {};

    //     p.searchType = data.searchType;
    //     // Job titles
    //     if (jobTitleList.length) p.jobTitleKeyword = jobTitleList.join(" OR ");
    //     if (data.jobTitleKeywords?.length) p.jobTitleTypes = data.jobTitleKeywords;
    //     // Skills + years of experience
    //     if (skillList.length) p.skillsKeyword = skillList.join(" OR ");
    //     if (skillList.length && yoeList.length)
    //         p.skillYearsOfExperience = skillList.map((s, i) => yoeList[i] ? `${s}|${yoeList[i]}` : s);
    //     // Companies
    //     if (companyList.length) p.companyKeyword = companyList.join(" OR ");
    //     // Contact
    //     if (data.hasEmail) p.hasEmail = true;
    //     if (data.hasPhoneNumber) p.hasPhoneNumber = true;
    //     if (switchGroup.length) p.likelyToSwitch = true;
    //     // Experience range
    //     const hasExpRange = data.yearsExperienceMin != null || data.yearsExperienceMax != null;
    //     if (hasExpRange) {
    //         p.yearsExperience = {};
    //         if (data.yearsExperienceMin != null) p.yearsExperience.min = data.yearsExperienceMin;
    //         if (data.yearsExperienceMax != null) p.yearsExperience.max = data.yearsExperienceMax;
    //     }
    //     // Background
    //     if (data.educationDegree?.length) p.educationDegree = data.educationDegree.join(",");
    //     if (data.socialProfiles?.length) p.socialProfiles = data.socialProfiles;
    //     if (data.language) p.language = data.language;

    //     // Work eligibility
    //     if (data.employmentType?.length) p.employmentType = data.employmentType.join(",");
    //     if (data.workPermitLocation?.length) p.workPermitLocation = data.workPermitLocation.join(" OR ");
    //     if (data.workPermit?.length) p.workPermit = data.workPermit.join(",");
    //     // Preferences
    //     if (data.travelPreference != null) p.travelPreference = data.travelPreference;

    //     // Compensation
    //     const hasComp = data.compensationMin != null || data.compensationMax != null || data.includeUnspecified;
    //     if (hasComp) {
    //         p.compensation = { currency: data.currency, frequency: data.frequency, includeUnspecified: data.includeUnspecified };
    //         if (data.compensationMin != null) p.compensation.min = data.compensationMin;
    //         if (data.compensationMax != null) p.compensation.max = data.compensationMax;
    //     }
    //     // Misc
    //     if (data.onlyWithSecurityClearance) p.onlyWithSecurityClearance = true;
    //     if (data.dateResumeLastUpdated) p.dateResumeLastUpdated = data.dateResumeLastUpdated;
    //     if (data.lastActive) p.lastActive = data.lastActive;
    //     if (workSetting) p.workSetting = workSetting;
    //     return p;
    // };
    const buildApiPayload = (data: any): Record<string, any> => {
        const payload: any = {};

        if (data.jobTitleKeywords?.length) payload.jobTitleKeyword = data.jobTitleKeywords.join(" OR ");

        // Skills + years-of-experience
        if (data.skillsWithYoe?.length) {
            payload.skillYearsOfExperience = data.skillsWithYoe
                .map((s: SkillWithYoe) =>
                    s.years != null ? `${s.skill}|${s.years}` : `${s.skill}`
                )
                .filter(Boolean);
        }

        if (data.companyKeywords?.length) payload.companyKeyword = data.companyKeywords.join(" OR ");

        if (data.searchType) payload.searchType = data.searchType;
        if (data.hasEmail) payload.hasEmail = true;
        if (data.hasPhoneNumber) payload.hasPhoneNumber = true;
        if (data.likelyToSwitch) payload.likelyToSwitch = true;

        if (data.yearsExperienceMin != null || data.yearsExperienceMax != null) {
            payload.yearsExperience = { min: data.yearsExperienceMin, max: data.yearsExperienceMax };
        }

        if ((data.compensationMin != null || data.compensationMax != null) || data.currency || data.frequency) {
            payload.compensation = {
                min: data.compensationMin ?? undefined,
                max: data.compensationMax ?? undefined,
                currency: data.currency,
                frequency: data.frequency,
                includeUnspecified: (data.includeUnspecified !== undefined && data.includeUnspecified !== null) ? false : data.includeUnspecified,
            };
        }

        if (data.educationDegree?.length) payload.educationDegree = data.educationDegree.join(",");
        if (data.employmentType?.length) payload.employmentType = data.employmentType.join(", ");
        if (data.language) payload.language = data.language;
        if (data.socialProfiles?.length) payload.socialProfiles = data.socialProfiles;
        if (data.workPermit?.length) payload.workPermit = data.workPermit;
        if (data.workPermitLocation?.length) payload.workPermitLocation = data.workPermitLocation.join(",");

        if (data.onlyWithSecurityClearance) payload.onlyWithSecurityClearance = data.security;
        if (data.travelPreference != null) payload.travelPreference = data.travelPreference;
        if (switchGroup.length) payload.likelyToSwitch = true;

        if (data.dateResumeLastUpdated) payload.dateResumeLastUpdated = data.dateResumeLastUpdated;
        if (data.lastActive) payload.lastActive = data.lastActive;
        if (workSetting) payload.workSetting = [workSetting];

        return payload;
    };

    const resetAll = () => {
        reset({
            jobTitleKeywords: [],
            skillsWithYoe: [],
            companyKeywords: [],
            searchType: "Integrated",
            hasEmail: false,
            hasPhoneNumber: false,
            likelyToSwitch: false,
            yearsExperienceMin: null,
            yearsExperienceMax: null,
            educationDegree: [],
            socialProfiles: ["dice"],
            language: "",
            employmentType: [],
            workPermitLocation: [],
            workPermit: [],
            travelPreference: null,
            currency: "USD",
            compensationMin: null,
            compensationMax: null,
            frequency: "yearly",
            includeUnspecified: false,
            onlyWithSecurityClearance: false,
            dateResumeLastUpdated: null,
            lastActive: null
        });
        props.setForm2Data({ searchType: "Integrated" });
        setJobTitleInput(""); setJobTitleList([]);
        setSkillInput(""); setSkillList([]);
        setYoeInput(""); setYoeList([]);
        setCompanyInput(""); setCompanyList([]);
        setSwitchGroup([]);
        setWorkSetting("");
        setSkillsWithYoeList([]);
        setSkillYoeInput("");
    };

    const addSkillWithYoe = () => {
        const skill = skillInput.trim();
        const yoe = parseNumber(skillYoeInput);

        if (!skill) return;
        setSkillsWithYoeList(prev => [...prev, { skill, years: yoe }]);
        setSkillInput("");
        setSkillYoeInput("");
    };

    const removeSkill = (index: number) => setSkillsWithYoeList(prev => prev.filter((_, i) => i !== index));

    // const onSubmit = async (data: FormValues) => {
    //     const body = buildPayload(data);
    //     const response = await Fetch("SearchFilter", "POST", body, null, null);
    //     if (response) {
    //         resetAll();
    //         hideDialog();
    //         setReload(!reload);
    //         toast?.current?.show({ severity: "success", summary: "Successful", detail: "Search Filter Saved Successfully", life: 3000 });
    //     } else {
    //         toast?.current?.show({ severity: "error", summary: "Error", detail: "Something Went Wrong", life: 3000 });
    //     }
    // };
    const onSubmit = async (data: any) => {
        // Inject UI lists into form data
        data.jobTitleKeywords = jobTitleList;
        data.skillsWithYoe = skillsWithYoeList.map((el: SkillWithYoe) => {
            if (el.years === undefined || el.years === null || el.years === 0) {
                return {
                    skill: el.skill,
                    years: null
                };
            } else return {
                skill: el.skill,
                years: el.years
            };
        });
        data.companyKeywords = companyList;

        const body = buildApiPayload(data);
        props.setForm2(data);
        props.setForm2Data(body);
        // props.setReload(!(props.reload));
        // const response = await Fetch("SearchFilter", "POST", body, null, null);

        // if (response) {
        //     resetAll();
        //     // hideDialog();
        //     props?.setReload(!(props?.reload));
        //     props?.toast?.current?.show({ severity: "success", summary: "Success", detail: "Search filter saved", life: 3000 });
        // } else {
        //     props?.toast?.current?.show({ severity: "error", summary: "Error", detail: "Failed to save", life: 3000 });
        // }
    };

    const filterTags = useMemo<FilterTag[]>(() => {
        const tags: FilterTag[] = [];

        // Job title keywords
        jobTitleList.forEach((t, i) => tags.push({
            id: `jt-${i}`, label: `Keyword: ${t}`, filled: true,
            onRemove: () => removeAt(i, setJobTitleList),
        }));

        // Job title types 
        (values.jobTitleKeywords as string[] ?? []).forEach((type: string, i: number) => tags.push({
            id: `jtt-${i}`, label: `Title type: ${type}`, filled: true,
            onRemove: () => reset({ ...values, jobTitleKeywords: (values.jobTitleKeywords as string[] ?? []).filter((_: string, idx: number) => idx !== i) }),
        }));

        // Skills 
        // skillList.forEach((s, i) => tags.push({
        //     id: `sk-${i}`,
        //     label: yoeList[i] ? `${s} >= ${yoeList[i]} yrs` : `Skill: ${s}`,
        //     filled: false,
        //     onRemove: () => { removeAt(i, setSkillList); removeAt(i, setYoeList); },
        // }));
        skillsWithYoeList.forEach((s, i) => tags.push({
            id: `sk-${i}`,
            label: `${s.skill} ${s.years != null ? `>= ${s.years} yrs` : ""}`,
            filled: false,
            onRemove: () => { removeSkill(i); },
        }));

        // Companies
        companyList.forEach((c, i) => tags.push({
            id: `co-${i}`, label: `Company: ${c}`, filled: false,
            onRemove: () => removeAt(i, setCompanyList),
        }));

        if (values.hasEmail)
            tags.push({ id: "email", label: "Has Email", filled: true, onRemove: () => reset({ ...values, hasEmail: false }) });

        if (values.hasPhoneNumber)
            tags.push({ id: "phone", label: "Has Phone", filled: true, onRemove: () => reset({ ...values, hasPhoneNumber: false }) });

        // Likely to Switch chips
        switchGroup.forEach((s: string, i: number) => tags.push({
            id: `sw-${i}`,
            label: s === "most" ? "Most Likely to Switch" : "Possibly Willing to Switch",
            filled: true,
            onRemove: () => setSwitchGroup((prev) => prev.filter((_, idx) => idx !== i)),
        }));

        if (values.yearsExperienceMin != null || values.yearsExperienceMax != null) {
            const min = values.yearsExperienceMin;
            const max = values.yearsExperienceMax;
            const label = min != null && max != null ? `Exp: ${min}-${max} yrs` : min != null ? `Exp >= ${min} yrs` : `Exp <= ${max} yrs`;
            tags.push({ id: "exp", label, filled: false, onRemove: () => reset({ ...values, yearsExperienceMin: null, yearsExperienceMax: null }) });
        }

        (values.educationDegree as string[] ?? []).forEach((e: string, i: number) => tags.push({
            id: `edu-${i}`, label: `Edu: ${e}`, filled: false,
            onRemove: () => reset({ ...values, educationDegree: (values.educationDegree as string[] ?? []).filter((_: string, idx: number) => idx !== i) }),
        }));

        (values.socialProfiles as string[] ?? []).forEach((s: string, i: number) => tags.push({
            id: `soc-${i}`, label: `Social: ${s}`, filled: false,
            onRemove: () => reset({ ...values, socialProfiles: (values.socialProfiles as string[] ?? []).filter((_: string, idx: number) => idx !== i) }),
        }));

        if (values.language)
            tags.push({ id: "lang", label: `Lang: ${values.language}`, filled: false, onRemove: () => reset({ ...values, language: "" }) });

        (values.employmentType as string[] ?? []).forEach((e: string, i: number) => tags.push({
            id: `emp-${i}`, label: e, filled: false,
            onRemove: () => reset({ ...values, employmentType: (values.employmentType as string[] ?? []).filter((_: string, idx: number) => idx !== i) }),
        }));

        (values.workPermitLocation as string[] ?? []).forEach((c: string, i: number) => tags.push({
            id: `ac-${i}`, label: `Country: ${c}`, filled: false,
            onRemove: () => reset({ ...values, workPermitLocation: (values.workPermitLocation as string[] ?? []).filter((_: string, idx: number) => idx !== i) }),
        }));

        (values.workPermit as string[] ?? []).forEach((w: string, i: number) => tags.push({
            id: `wp-${i}`, label: w, filled: false,
            onRemove: () => reset({ ...values, workPermit: (values.workPermit as string[] ?? []).filter((_: string, idx: number) => idx !== i) }),
        }));

        if (values.compensationMin != null || values.compensationMax != null) {
            const sym = { USD: "$", GBP: "£", EUR: "€", CAD: "CA$" }[values.currency ?? "USD"] ?? "$";
            const freq = values.frequency === "yearly" ? "yr" : "hr";
            const min = values.compensationMin;
            const max = values.compensationMax;
            const label = min != null && max != null ? `${sym}${min}-${sym}${max}/${freq}` : min != null ? `${sym}${min}+/${freq}` : `Up to ${sym}${max}/${freq}`;
            tags.push({ id: "comp", label, filled: false, onRemove: () => reset({ ...values, compensationMin: null, compensationMax: null }) });
        }

        if (values.onlyWithSecurityClearance)
            tags.push({ id: "sec", label: "Security Clearance", filled: true, onRemove: () => reset({ ...values, onlyWithSecurityClearance: false }) });

        if (values.travelPreference != null) {
            const label = values.travelPreference === 0 ? "No Travel" : `Travel <= ${values.travelPreference}%`;
            tags.push({ id: "travel", label, filled: false, onRemove: () => reset({ ...values, travelPreference: null }) });
        }

        if (values.dateResumeLastUpdated)
            tags.push({ id: "resume", label: `Resume <= ${values.dateResumeLastUpdated}d`, filled: false, onRemove: () => reset({ ...values, dateResumeLastUpdated: null }) });

        if (values.lastActive)
            tags.push({ id: "active", label: `Active <= ${values.lastActive}d`, filled: false, onRemove: () => reset({ ...values, lastActive: null }) });

        if (workSetting)
            tags.push({ id: "ws", label: workSetting.charAt(0).toUpperCase() + workSetting.slice(1), filled: false, onRemove: () => setWorkSetting("") });

        return tags;
    }, [values, jobTitleList, skillList, yoeList, companyList, switchGroup, workSetting, reset]);

    useEffect(() => {
        reset({ searchType: "Integrated" });
    }, []);

    return (
        <Card className="border-top-3 border-primary mt-3" style={{ overflow: 'hidden' }}>
            <div className="flex justify-content-between align-items-center mb-2">
                <div className="flex gap-2">
                    <Button label="Expand All" className="p-button-success mr-2 bg-primary" type="button" onClick={handleExpandAll} />
                    <Button label="Collapse All" className="p-button-success mr-2 bg-primary" type="button" onClick={handleCollapseAll}
                    />
                </div>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <FilterSummaryBar tags={filterTags} />

                <Accordion multiple activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>

                    {/* ── Job Titles ── */}
                    <AccordionTab header="Job Titles">
                        <p className="text-md text-600">
                            Filter by current, preferred or previous job titles.
                        </p>

                        <div className="flex flex-wrap gap-2">
                            <Controller name="jobTitleKeywords" control={control} render={({ field }) => (
                                <MultiSelect {...field} options={JOB_TYPE_OPTIONS} placeholder="Title type (Current / Preferred / Previous)" display="chip" className="w-full " />
                            )} />

                            <TagInput value={jobTitleInput} onChange={setJobTitleInput} onAdd={() => addToList(jobTitleInput, setJobTitleInput, setJobTitleList)} placeholder="Job title or Boolean" className="w-full" />
                        </div>
                    </AccordionTab>

                    {/* ── Skills ── */}
                    {/* <AccordionTab header="Skills">
                        <div className="flex flex-row gap-3">
                            <TagInput value={skillInput} onChange={setSkillInput} onAdd={() => addToList(skillInput, setSkillInput, setSkillList)} placeholder="Skill or Boolean"
                            />
                            <TagInput value={yoeInput} onChange={setYoeInput} onAdd={() => addToList(yoeInput, setYoeInput, setYoeList, /^\d+$/.test(yoeInput))} placeholder="Years of experience (optional)" disabled={yoeInput !== "" && !/^\d+$/.test(yoeInput)}
                            />
                        </div>
                    </AccordionTab> */}
                    {/* <AccordionTab header="Skills">
                        <div className="flex flex-column gap-2">
                            <InputText placeholder="Skill" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} className="w-full "
                            />
                            <InputText placeholder="Years of Experience" value={skillYoeInput} onChange={(e) => setSkillYoeInput(e.target.value)} className="w-full "
                            />
                            <Button icon="pi pi-plus" disabled={!skillInput.trim()} onClick={addSkillWithYoe}
                            />
                        </div>
                    </AccordionTab> */}

                    {/* ── Companies ── */}
                    <AccordionTab header="Companies">
                        <TagInput value={companyInput} onChange={setCompanyInput} onAdd={() => addToList(companyInput, setCompanyInput, setCompanyList)} placeholder="Company or Boolean" className="w-full "
                        />
                    </AccordionTab>

                    {/* ── Profile Source ── */}
                    <AccordionTab header="Profile Source">
                        <p className="text-md text-600 ">Social profiles are sourced from open web data.</p>
                        <Controller name="socialProfiles" control={control} render={({ field }) => (
                            <div className="flex gap-4">
                                <div className="flex align-items-center gap-2">
                                    <RadioButton inputId="socialProfiles" value={["all"]} checked={(field.value || []).filter((el: string) => el === "all").length > 0} onChange={() => field.onChange(["all"])} />
                                    <label htmlFor="socialProfiles">Any (Dice &amp; Social)</label>
                                </div>
                                <div className="flex align-items-center gap-2">
                                    <RadioButton inputId="socialProfiles" value={["dice"]} checked={(field.value || []).filter((el: string) => el === "dice").length > 0} onChange={() => field.onChange(["dice"])} />
                                    <label htmlFor="socialProfiles">Dice Only</label>
                                </div>
                            </div>
                        )}
                        />
                        {fieldError("socialProfiles") && <small className="p-error">{fieldError("socialProfiles")}</small>}
                    </AccordionTab>

                    {/*  Contact Method  */}
                    <AccordionTab header="Contact Method">
                        <div className="flex gap-5">
                            <div className="flex align-items-center gap-2 w-full">
                                <Controller name="hasEmail" control={control} render={({ field }) => (
                                    <Checkbox inputId="hasEmail" checked={!!field.value} onChange={(e) => field.onChange(!!e.checked)} />
                                )} />
                                <label htmlFor="hasEmail">Email</label>
                            </div>
                            <div className="flex align-items-center gap-2 w-full">
                                <Controller name="hasPhoneNumber" control={control} render={({ field }) => (
                                    <Checkbox inputId="hasPhone" checked={!!field.value} onChange={(e) => field.onChange(!!e.checked)} />
                                )} />
                                <label htmlFor="hasPhone">Phone</label>
                            </div>
                        </div>
                    </AccordionTab>

                    {/* Likely to Switch  */}
                    <AccordionTab header="Likely to Switch">
                        <MultiSelect value={switchGroup} options={SWITCH_OPTIONS} onChange={(e) => setSwitchGroup(e.value)} placeholder="Select switch likelihood" display="chip" className="w-full"
                        />
                    </AccordionTab>

                    {/*  Total Years Experience  */}
                    <AccordionTab header="Total Years Experience">
                        <div className="flex gap-2">
                            <Controller name="yearsExperienceMin" control={control} render={({ field }) => (
                                <InputText
                                    {...field} value={field.value ?? ""} placeholder="Min years" className={`w-full ${fieldError("yearsExperienceMin") ? "p-invalid" : ""}`} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))} />
                            )} />
                            <Controller name="yearsExperienceMax" control={control} render={({ field }) => (
                                <InputText
                                    {...field} value={field.value ?? ""} placeholder="Max years" className={`w-full ${fieldError("yearsExperienceMax") ? "p-invalid" : ""}`} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                                />
                            )} />
                        </div>
                        {fieldError("yearsExperienceMin") && <small className="p-error">{fieldError("yearsExperienceMin")}</small>}
                        {fieldError("yearsExperienceMax") && <small className="p-error">{fieldError("yearsExperienceMax")}</small>}
                    </AccordionTab>

                    {/*  Education  */}
                    <AccordionTab header="Education">
                        <Controller name="educationDegree" control={control} render={({ field }) => (
                            <MultiSelect {...field} options={EDUCATION_OPTIONS} placeholder="Select education background" display="chip" className="w-full" />
                        )} />
                    </AccordionTab>

                    {/*  Social Media  */}
                    <AccordionTab header="Social Media">
                        <Controller name="socialProfiles" control={control} render={({ field }) => (
                            <MultiSelect {...field} options={SOCIAL_OPTIONS} placeholder="Select social profiles" display="chip" className="w-full" />
                        )} />
                    </AccordionTab>

                    {/*  Languages  */}
                    <AccordionTab header="Languages">
                        <Controller name="language" control={control} render={({ field }) => (
                            <MultiSelect
                                value={field.value ? field.value.split(" OR ") : []} options={LANGUAGE_OPTIONS} onChange={(e) => field.onChange((e.value as string[]).join(" OR "))} placeholder="Select languages" display="chip" className="w-full"
                            />
                        )} />
                    </AccordionTab>

                    {/*  Resume Last Updated   */}
                    <AccordionTab header="Resume Last Updated">
                        <p className="text-sm text-600 mb-2">Max days since resume was updated on Dice (max 2500)</p>
                        <Controller name="dateResumeLastUpdated" control={control} render={({ field }) => (
                            <InputText
                                {...field} value={field.value ?? ""} placeholder="# of days" className={`w-full ${fieldError("dateResumeLastUpdated") ? "p-invalid" : ""}`} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                            />
                        )} />
                        {fieldError("dateResumeLastUpdated") && <small className="p-error">{fieldError("dateResumeLastUpdated")}</small>}
                    </AccordionTab>

                    {/* Last Active on Dice  */}
                    <AccordionTab header="Last Active on Dice">
                        <p className="text-sm text-600 mb-2">Max days since last activity on Dice (max 2500)</p>
                        <Controller name="lastActive" control={control} render={({ field }) => (
                            <InputText  {...field} value={field.value ?? ""} placeholder="# of days" className={`w-full ${fieldError("lastActive") ? "p-invalid" : ""}`} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                            />
                        )} />
                        {fieldError("lastActive") && <small className="p-error">{fieldError("lastActive")}</small>}
                    </AccordionTab>

                    {/* ── Employment Type ── */}
                    <AccordionTab header="Employment Type">
                        <Controller name="employmentType" control={control} render={({ field }) => (
                            <MultiSelect {...field} options={EMPLOYMENT_OPTIONS} placeholder="Select employment types" display="chip" className="w-full" />
                        )} />
                    </AccordionTab>

                    {/* ── Authorized Countries ── */}
                    <AccordionTab header="Authorized Countries">
                        <Controller name="workPermitLocation" control={control} render={({ field }) => (
                            <MultiSelect {...field} options={COUNTRY_OPTIONS} placeholder="Select authorized countries" display="chip" className="w-full" />
                        )} />
                    </AccordionTab>

                    {/* ── Work Permits ── */}
                    <AccordionTab header="Work Permits">
                        <Controller name="workPermit" control={control} render={({ field }) => (
                            <MultiSelect {...field} options={WORK_PERMIT_OPTIONS} placeholder="Select work permits" display="chip" className="w-full" />
                        )} />
                    </AccordionTab>

                    {/* ── Compensation ── */}
                    <AccordionTab header="Compensation">
                        <div className="flex flex-column gap-3">

                            <div className="flex flex-wrap align-items-center gap-3">
                                <Controller name="currency" control={control} render={({ field }) => (
                                    <Dropdown {...field} options={CURRENCY_OPTIONS} className="w-full" placeholder="Choose currency" />
                                )} />
                                <Controller name="compensationMin" control={control} render={({ field }) => (
                                    <InputText
                                        {...field} value={field.value ?? ""} placeholder="Min" className={`w-full ${fieldError("compensationMin") ? "p-invalid" : ""}`} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                                    />
                                )} />
                                <Controller name="compensationMax" control={control} render={({ field }) => (
                                    <InputText
                                        {...field} value={field.value ?? ""} placeholder="Max" className={`w-full ${fieldError("compensationMax") ? "p-invalid" : ""}`} onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                                    />
                                )} />
                                <Controller name="frequency" control={control} render={({ field }) => (
                                    <Dropdown {...field} options={FREQUENCY_OPTIONS} className="w-full " placeholder="Choose frequency" />
                                )} />
                            </div>

                            {fieldError("compensationMin") && <small className="p-error">{fieldError("compensationMin")}</small>}
                            {fieldError("compensationMax") && <small className="p-error">{fieldError("compensationMax")}</small>}

                            <Controller name="includeUnspecified" control={control} render={({ field }) => (
                                <div className="flex align-items-center gap-2">
                                    <InputSwitch checked={!!field.value} onChange={(e) => field.onChange(e.value)} />
                                    <label>Include candidates without compensation disclosed</label>
                                </div>
                            )} />

                        </div>
                    </AccordionTab>

                    {/* ── Security Clearance ── */}
                    <AccordionTab header="Security Clearance">
                        <Controller name="onlyWithSecurityClearance" control={control} render={({ field }) => (
                            <div className="flex align-items-center gap-2">
                                <Checkbox inputId="security" checked={!!field.value} onChange={(e) => field.onChange(!!e.checked)} />
                                <label htmlFor="security">Has Security Clearance</label>
                            </div>
                        )} />
                    </AccordionTab>

                    {/* ── Travel Preference ── */}
                    <AccordionTab header="Travel Preference">
                        <Controller name="travelPreference" control={control} render={({ field }) => (
                            <div className="flex flex-wrap gap-3">
                                {TRAVEL_OPTIONS.map((opt) => (
                                    <div key={opt.id} className="flex align-items-center gap-2">
                                        <RadioButton inputId={opt.id} name="travel" value={opt.value} checked={field.value === opt.value} onChange={() => field.onChange(opt.value)} />
                                        <label htmlFor={opt.id}>{opt.label}</label>
                                    </div>
                                ))}
                            </div>
                        )} />
                    </AccordionTab>

                    {/* ── Desired Work Setting ── */}
                    <AccordionTab header="Desired Work Setting">
                        <div className="flex flex-wrap gap-3">
                            {WORK_SETTING_OPTIONS.map((opt) => (
                                <div key={opt.id} className="flex align-items-center gap-2">
                                    <Checkbox inputId={opt.id} value={opt.value} checked={workSetting === opt.value} onChange={() => setWorkSetting(workSetting === opt.value ? "" : opt.value)} />
                                    <label htmlFor={opt.id}>{opt.label}</label>
                                </div>
                            ))}
                        </div>
                    </AccordionTab>
                </Accordion>

                <div className="flex justify-content-end gap-2 mt-4">
                    <Button loading={props.loading} disabled={props.loading} label="Save" type="submit" className="p-button-primary" />
                    <Button loading={props.loading} disabled={props.loading} label="Clear" type="button" className="p-button-outlined p-button-secondary" onClick={resetAll} />
                </div>

            </form>
        </Card>
    );
};